/**
 * Phase 5 — useConversations TanStack Query hook.
 *
 * Fetches GET /api/ai/conversations for the current session member.
 * queryKey ['ai', 'conversations'] is the stable key used by
 * useAssistantMutations to invalidate on create/delete.
 */

import { useQuery } from '@tanstack/react-query';

export interface AiConversation {
  id: string;
  member_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  removed_at: string | null;
}

export interface AiConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  content_json: string;
  tool_name: string | null;
  tool_call_id: string | null;
  filter_status: 'passed' | 'corrected' | null;
  food_adjacent: boolean;
  created_at: string;
}

export function useConversations(opts: { enabled?: boolean } = {}) {
  return useQuery<{ conversations: AiConversation[] }>({
    queryKey: ['ai', 'conversations'],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const res = await fetch('/api/ai/conversations', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { conversations: AiConversation[] };
    },
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery<{ messages: AiConversationMessage[] }>({
    queryKey: ['ai', 'conversation-messages', conversationId],
    enabled: conversationId !== null,
    queryFn: async () => {
      const res = await fetch(`/api/ai/conversations/${conversationId}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { messages: AiConversationMessage[] };
    },
  });
}
